import { namespace, Point, Line } from '../../../../../../FrontendBundle/Resources/js/ofcs';
import AbstractResizeHandle from './abstractResizeHandle';
import ResizeHandleMarker from './resizeHandleMarker';

/**
 * Represents the resize handle on a svg canvas for connector.
 *
 * @class   Ofcs.Editor.Svg.Handle.ResizeConnectorHandle
 * @extends Ofcs.Editor.Svg.Handle.AbstractResizeHandle
 */
export default class ResizeConnectorHandle extends AbstractResizeHandle {
    /**
     * Initializes a resize handle
     *
     * @param {Ofcs.Editor.Item.ConnectorItem} connector Item which should be resized
     * @param {Ofcs.Editor.Svg.Canvas} canvas Canvas which receives the handle
     */
    constructor(...args) {
        super(...args);
        this.helper = new Ofcs.Editor.Svg.Handle.CutangleHelper(this.item.parentItem);
    }

    /**
     * Initialize drag handles.
     *
     * @protected
     *
     * @param {Ofcs.Editor.Item.ConnectorItem} item
     */
    initializePointers() {
        this.handles = {
            startPoint: new ResizeHandleMarker('startPoint', 'endPoint', this.createPointerElement()),
            endPoint: new ResizeHandleMarker('endPoint', 'startPoint', this.createPointerElement()),
        };
    }

    initializeCommand() {
        this.command = new Ofcs.Editor.Command.ResizeConnectorItemCommand(this.item);
    }

    /**
     * Updates the position of internal DOM elements based on the attributes
     * of #circle.
     *
     * @protected
     */
    updateElementsPositions() {
        const { options } = this.canvas;
        const start = this.item.get('line').startPoint.clone();
        const end = this.item.get('line').endPoint.clone();
        const zoomMatrix = new Ofcs.Matrix();

        zoomMatrix.translate(-this.HANDLE_SIZE / 2, -this.HANDLE_SIZE / 2)
            .scale(options.zoom)
            .translate(options.panX, options.panY);

        start.transform(zoomMatrix);
        end.transform(zoomMatrix);

        this.handles.startPoint.element.attr(start);
        this.handles.endPoint.element.attr(end);
    }

    /**
     * Callback method which is called when dragging of pointer starts.
     *
     * Based on the dragged handle the correct resizing function (closure)
     * is generated and stored in the member dragFn, which is called on each drag event.
     *
     * @protected
     *
     * @param {Object} event Drag event. See CanvasDraggable#drag
     */
    onPointerDragStart(event) {
        this.passivePoint = this.handles.startPoint.element[0] === event.startTarget ? 'endPoint' : 'startPoint';
        this.targetSegment = this.item.getTargetSegment();

        super.onPointerDragStart(event);
    }

    /**
     * Callback method which is called each movement while dragging.
     *
     * It is calling the dragFn closure created in the dragStart callback.
     *
     * @protected
     *
     * @param {Object} event Drag event. See CanvasDraggable#drag
     */
    onPointerDrag(event) {
        let i = 0;
        const projection = this.canvas.getPointByPixels(event.pageX, event.pageY);
        this.helper.updateCursorProjections(projection.x, projection.y);

        while (this.helper.cursorProjections[i]) {
            let temp = this.helper.cursorProjections[i].projectIntersection(this.targetSegment);
            let start;
            let end;

            if (temp.first && temp.second) {
                projection.x = temp.x;
                projection.y = temp.y;

                const otherPoint = this.item.get('line')[this.passivePoint];
                if (otherPoint.getDistanceTo(projection) > (2 * this.item.SIDE_MARGIN)) {
                    start = this.passivePoint === 'endPoint' ? projection : otherPoint;
                    end = this.passivePoint === 'endPoint' ? otherPoint : projection;
                    temp = new Line(start, end);
                    const diff = Math.abs(temp.getAngle() - this.item.get('line').getAngle());
                    if (diff < 1 || 360 - diff < 1) {
                        // Remove!!!
                    } else {
                        temp = new Line(end, start);
                        this.passivePoint = this.passivePoint === 'endPoint' ? 'startPoint' : 'endPoint';
                    }

                    this.command.setLine(temp);
                } else {
                    this.command.setLine(this.item.get('line').clone());
                }

                i = this.helper.cursorProjections.length - 1;
            } else if (temp.first || temp.second) {
                const target = new Point(temp.x, temp.y);
                const dist1 = this.targetSegment.startPoint.getDistanceTo(target);
                const dist2 = this.targetSegment.endPoint.getDistanceTo(target);

                start = dist1 > dist2 ? this.item.get('line').startPoint : this.targetSegment.startPoint;
                end = dist1 > dist2 ? this.targetSegment.endPoint : this.item.get('line').endPoint;

                this.command.setLine(new Line(start, end));
            }

            i += 1;
            if (i === this.helper.cursorProjections.length) {
                this.canvas.getCommandExecutor().execute(this.command);
            }
        }
    }

    /**
     * Callback which is called each time an attribute of #item is changed.
     *
     * @protected
     *
     * @param {Ofcs.Editor.Item.ItemInterface} item The item which was modified. Equal to #item
     */
    onItemChange(item) {
        super.onItemChange(item);
        if (item.changed.line) {
            this.updateElementsPositions();
        }
    }
}

namespace('Ofcs.Editor.Svg.Handle');
window.Ofcs.Editor.Svg.Handle.ResizeConnectorHandle = ResizeConnectorHandle;
